import { type ManagerColor } from "@salesboard/contracts";
import { Tooltip } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/initials";
import { BADGE_TEXT, MANAGER_HEX, badgeFill } from "@/lib/manager-colors";

/**
 * The owner's initials circle — one rendering shared by the board card and the
 * opportunities list, so a scan down either surface reads the same ownership the same
 * way. Originally the card's own `OwnerBadge` (see
 * [card-density](../../../../specs/board/card-density.md)); extracted here once the list
 * gained the same badge, before the name, per specs/pipeline-polish.md — two copies of
 * this circle is exactly how the two surfaces would start disagreeing about a colour.
 *
 * The circle is filled at full strength with the owner's `--manager-*` colour — not a
 * `color-mix` tint, which is far too pale to sit under text — and an owner without a
 * colour gets the same muted treatment a status pill uses elsewhere, rather than looking
 * like a broken assignment.
 *
 * The full name lives in the shared `Tooltip`, never the native `title`, per
 * [ui-patterns.md](../../../../.claude/docs/ui-patterns.md#tooltips); it is data, not
 * catalogue text, so it needs no i18n key. Callers that also render the name as visible
 * text (the list) get no duplicate accessible name from this component beyond the
 * tooltip — the caller's own text carries that.
 */
export function OwnerBadge({ name, color }: { name: string; color: ManagerColor | null }): React.JSX.Element {
  return (
    <Tooltip content={name}>
      <span
        className={cn(
          // Size and type scale with the board — see `.board-owner-badge` in index.css.
          "board-owner-badge inline-flex shrink-0 items-center justify-center rounded-full font-semibold leading-none",
          !color && "bg-muted text-muted-foreground",
        )}
        style={color ? { backgroundColor: badgeFill(MANAGER_HEX[color]), color: BADGE_TEXT } : undefined}
      >
        {initials(name)}
      </span>
    </Tooltip>
  );
}
